import {
  Alert,
  Card,
  Center,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { createContext, useContext } from "react";
import { IconAlertCircle } from "@tabler/icons-react";
import InsightsAnalysisSection from "./InsightsAnalysisSection";

export const SectionPartsContext = createContext(null);

export const usePartShown = (part) => {
  const parts = useContext(SectionPartsContext);
  return !part || parts?.[part] !== false;
};

export const Stat = ({ part, label, value, hint }) =>
  usePartShown(part) ? (
    <Stack
      gap={2}
      py="sm"
      className="insights-stat"
      style={{ minWidth: 0, overflowWrap: "anywhere" }}
    >
      <Text size="xs" c="dimmed" fw={600} tt="uppercase" lh={1.3}>
        {label}
      </Text>
      <Title order={3}>{value}</Title>
      {hint && (
        <Text size="xs" c="dimmed">
          {hint}
        </Text>
      )}
    </Stack>
  ) : null;

export const formatPct = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(1)}%`;
};

export const formatNum = (value) =>
  Number.isFinite(Number(value)) ? Number(value).toLocaleString() : "—";

export const ChartPair = ({ children }) => (
  <div className="insights-chart-pair">{children}</div>
);

function SectionShell({
  id,
  title,
  description,
  isPending,
  isError,
  error,
  eventId,
  analysisKey,
  headerRight,
  stats,
  lead,
  children,
}) {
  return (
    <Card withBorder padding="lg" radius="md" id={id}>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
          <Stack gap={2}>
            <Title order={3}>{title}</Title>
            {description && (
              <Text c="dimmed" size="sm">
                {description}
              </Text>
            )}
          </Stack>
          {headerRight}
        </Group>

        {isError && (
          <Alert
            color="red"
            icon={<IconAlertCircle size={16} />}
            title={`Failed to load ${title.toLowerCase()}`}
          >
            {error?.message || "Could not load section data."}
          </Alert>
        )}

        {isPending ? (
          <Center mih={120}>
            <Loader />
          </Center>
        ) : (
          <>
            {(stats || lead) && (
              <div className="insights-lead-row">
                <div className="insights-stat-rail">{stats}</div>
                <div className="insights-lead">{lead}</div>
              </div>
            )}
            {children}
          </>
        )}

        {analysisKey && (
          <InsightsAnalysisSection
            event={{ id: eventId }}
            sectionKey={analysisKey}
            title="Analysis"
          />
        )}
      </Stack>
    </Card>
  );
}

export const EmptyState = ({ mih = 120 }) => (
  <Center mih={mih}>
    <Text c="dimmed">No data</Text>
  </Center>
);

export const hasChartData = (rows, keys) => {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  const fields = Array.isArray(keys) ? keys : [keys];
  return rows.some((row) =>
    fields.some((k) => {
      const n = Number(row?.[k]);
      return Number.isFinite(n) && n > 0;
    })
  );
};

export default SectionShell;
